<template>
  <div
    v-if="isExpanded"
    class="footnote-editor mt-2 p-2"
    :class="[bgClass, { 'footnote-editor--bordered': bordered }]"
  >
    <label v-if="showLabel" for="footnote-text-input" class="footnote-label heading"
      >FOOTNOTE</label
    >
    <div class="field mb-2">
      <div class="control">
        <input
          id="footnote-text-input"
          :value="text?.trim() || ''"
          type="text"
          class="input is-small"
          :placeholder="placeholder"
          data-test-id="footnote-text-input"
          aria-label="Footnote text"
          @change="$emit('update:text', $event.target.value)"
        />
      </div>
    </div>
    <div class="is-flex is-align-items-center mb-1">
      <label class="checkbox is-size-7 is-flex is-align-items-center">
        <input
          type="checkbox"
          class="mr-1"
          :checked="isCustomSymbolActive"
          data-test-id="footnote-custom-symbol-checkbox"
          @change="handleCheckboxChange"
        />
        <span>Custom symbol</span>
        <input
          v-if="isCustomSymbolActive"
          :value="customChar"
          type="text"
          class="input is-small ml-1"
          maxlength="1"
          pattern="[1-9A-Za-z]"
          style="width: 1.8rem; text-align: center; padding: 0"
          data-test-id="footnote-custom-char-input"
          @keydown="preventInvalidFootnoteChar"
          @input="$emit('update:customChar', sanitizeFootnoteChar($event.target.value))"
          @focus="isInputFocused = true"
          @blur="handleInputBlur"
        />
      </label>
      <IconHint content="1-9 or A-Z are valid choices for the custom symbol." class="ml-1" />
    </div>
    <div>
      <label class="checkbox is-size-7 is-flex is-align-items-center">
        <input
          type="checkbox"
          class="mr-1"
          :checked="onAmount"
          data-test-id="footnote-on-amount-checkbox"
          @change="$emit('update:onAmount', $event.target.checked)"
        />
        <span>{{ onAmountLabel }}</span>
      </label>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch } from "vue";
  import {
    sanitizeFootnoteChar,
    preventInvalidFootnoteChar,
    getNextAvailableFootnoteChar,
  } from "./utils/formatting";
  import { useSupplementLabelStore } from "../stores/supplement-label-store";
  import IconHint from "../icon-hint.vue";

  const store = useSupplementLabelStore();

  const props = defineProps({
    isExpanded: { type: Boolean, required: true },
    text: { type: String, default: "" },
    customChar: { type: String, default: "" },
    onAmount: { type: Boolean, default: false },
    placeholder: { type: String, default: "Custom footnote text" },
    bgClass: { type: String, default: "bg-light-gray" },
    onAmountLabel: { type: String, default: "Show next to amount instead of name" },
    showLabel: { type: Boolean, default: true },
    bordered: { type: Boolean, default: false },
  });

  const emit = defineEmits(["update:text", "update:customChar", "update:onAmount"]);

  const isCustomSymbolActive = ref(!!props.customChar);
  const isInputFocused = ref(false);

  watch(
    () => props.customChar,
    (newVal) => {
      if (newVal) {
        isCustomSymbolActive.value = true;
      } else if (!newVal && !isInputFocused.value) {
        // Only deactivate if empty AND not focused (e.g. external clear or blur actions)
        isCustomSymbolActive.value = false;
      }
    }
  );

  const handleCheckboxChange = (event) => {
    const checked = event.target.checked;
    isCustomSymbolActive.value = checked;
    if (checked) {
      const { used, lastType } = store.usedFootnoteSymbols;
      const nextChar = props.customChar || getNextAvailableFootnoteChar(used, lastType);
      emit("update:customChar", nextChar);
    } else {
      emit("update:customChar", "");
    }
  };

  const handleInputBlur = () => {
    isInputFocused.value = false;
    if (!props.customChar) {
      // If user clicks away and value is empty, cleanup by unchecking
      isCustomSymbolActive.value = false;
    }
  };
</script>

<style lang="scss" scoped>
  @import "~/label/css/labels/supplements/supplement_mixins";

  .footnote-editor {
    border-radius: 4px;
  }

  .footnote-label {
    display: block;
    margin-bottom: 0.35rem;
    letter-spacing: 0.1em;
  }

  .footnote-editor--bordered {
    border: 1px solid $border-standard;
  }
</style>
